import { useCallback, useMemo } from "react"

import { isInternalStopType } from "@/lib/haulageUtils"
import { findRouteInstanceOn } from "@/lib/plannedRouteMapping"
import type { PlannedRoute, RouteInstance } from "@/types/collections"

/** Planned stops carry no forecast, so it's read from the day's stop instances. */
export function useVolumeForecasts(
  routeInstances: RouteInstance[] | undefined,
  selectedDate: Date,
) {
  const volumeForecastByLocation = useMemo(() => {
    const map = new Map<string, number>()
    if (routeInstances) {
      for (const instance of routeInstances) {
        if (instance.stops) {
          for (const stop of instance.stops) {
            if (isInternalStopType(stop.stop_type)) {
              continue
            }

            if (stop.location_id && stop.volume_forecast !== undefined) {
              const current = map.get(stop.location_id) || 0
              map.set(stop.location_id, current + stop.volume_forecast)
            }
          }
        }
      }
    }
    return map
  }, [routeInstances])

  // Per stop: the by-location sum inflates when a location repeats.
  const getVolumeForecastForStop = useCallback(
    (route: PlannedRoute, stop: { locationId?: string }) => {
      if (!stop?.locationId) return 0
      const baseRouteId =
        route.id.includes("-") && route.id.match(/-\d{4}-\d{2}-\d{2}$/)
          ? route.id.replace(/-\d{4}-\d{2}-\d{2}$/, "")
          : route.id
      const ri = findRouteInstanceOn(routeInstances, baseRouteId, selectedDate)
      const instanceStop = ri?.stops?.find((s) => s.location_id === stop.locationId)
      return (instanceStop?.volume_forecast ?? 0) as number
    },
    [routeInstances, selectedDate],
  )

  // Fallback without stop context: the location sum, which can inflate.
  const getVolumeForecast = useCallback(
    (locationId: string) => {
      return volumeForecastByLocation.get(locationId) || 0
    },
    [volumeForecastByLocation],
  )

  return { getVolumeForecastForStop, getVolumeForecast }
}
